import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { isVoid } from "@/shared/utils";
import { noteApi, personApi } from "@/services/api";
import { buildNotesTableDataSource } from "@/entities/notes-table-v3";
import type { MaybeType } from "@/shared/types";
import type { Key } from "react";
import type { NotesState, NotesTableItem } from "../types";

const initialState: NotesState = {
  selectedNotes: [],
  selectedIds: [],
  activeNote: null,
  swipedNote: null,
  notes: [],
  persons: [],
  isLoading: false,
};

export const notesState = createSlice({
  name: "ideas",
  initialState,
  reducers: {
    openNoteById(state, action: PayloadAction<{ noteId: string }>) {
      const tableData = buildNotesTableDataSource({ notes: state.notes, persons: state.persons });
      const newNote = tableData.find((note) => note.id === action.payload.noteId);

      if (newNote) {
        if (!state.selectedNotes.some((note) => note.id === newNote.id)) {
          state.selectedNotes = [...state.selectedNotes, newNote];
        }
        state.activeNote = newNote;
      }
    },
    setNotes(state, action: PayloadAction<NotesTableItem[]>) {
      state.notes = action.payload;

      if (isVoid(state.selectedNotes)) {
        state.selectedNotes = [action.payload[0]];
        state.activeNote = action.payload[0];
      }
    },
    setActiveNote(state, action: PayloadAction<{ noteId: string }>) {
      const { noteId } = action.payload;

      state.activeNote = state.selectedNotes.find((note) => note.id === noteId) ?? null;
    },
    updateActiveNote(state, action: PayloadAction<NotesTableItem>) {
      const updatedNote = action.payload;

      const noteIndex = state.selectedNotes.findIndex((note) => note.id === updatedNote.id);

      if (noteIndex !== -1) {
        state.selectedNotes[noteIndex] = updatedNote;
      }
    },
    updateDraftNoteId(state, action: PayloadAction<{ tempId: string; newId: string }>) {
      const { tempId, newId } = action.payload;

      const noteIndex = state.selectedNotes.findIndex((note) => note.id === tempId);

      if (noteIndex !== -1) {
        state.selectedNotes[noteIndex].id = newId;
      }

      if (state.activeNote?.id === tempId) {
        state.activeNote.id = newId;
      }
    },
    addSelectedNote(state, action: PayloadAction<NotesTableItem>) {
      if (state.selectedNotes.some((note) => note.id === action.payload.id)) {
        return;
      }
      state.activeNote = action.payload;
      state.selectedNotes = [...state.selectedNotes, action.payload];
    },
    setSelectedNotes(state, action: PayloadAction<NotesTableItem[]>) {
      state.selectedNotes = action.payload;
    },
    removeSelectedNote(state, action: PayloadAction<{ noteId: string }>) {
      const { noteId } = action.payload;

      const newNotes = state.selectedNotes.filter((note) => note.id !== noteId);

      if (state.activeNote?.id === noteId && newNotes.length > 0) {
        state.activeNote = newNotes[0];
      }
      state.selectedNotes = newNotes;
    },
    setSelectedIds(state, action: PayloadAction<Key[]>) {
      state.selectedIds = action.payload;
    },
    setSwipedNote(state, action: PayloadAction<MaybeType<string>>) {
      state.swipedNote = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(personApi.endpoints.getAllPersons.matchFulfilled, (state, action) => {
        state.persons = action.payload.persons;
        state.isLoading = false;
      })
      .addMatcher(personApi.endpoints.getAllPersons.matchPending, (state) => {
        state.isLoading = true;
      })
      .addMatcher(personApi.endpoints.getAllPersons.matchRejected, (state) => {
        state.isLoading = false;
      })
      .addMatcher(noteApi.endpoints.getAllNotes.matchFulfilled, (state, action) => {
        state.notes = action.payload;
        state.isLoading = false;
      })
      .addMatcher(noteApi.endpoints.getAllNotes.matchPending, (state) => {
        state.isLoading = true;
      })
      .addMatcher(noteApi.endpoints.getAllNotes.matchRejected, (state) => {
        state.isLoading = false;
      })
      .addMatcher(noteApi.endpoints.updateNote.matchFulfilled, (state, action) => {
        const note = action.payload;

        const noteIndex = state.notes.findIndex((n) => n.id === note.id);

        if (noteIndex !== -1) {
          state.notes[noteIndex] = note;
        }
      });
  },
});
